import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/fetch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { IS_STAGING } from "@/features/config";
import { USAGE_QUERY_KEY } from "@/features/usage/constants";
import { fetchPermalink, huntClaim, recordVisit, summarizeEvidence } from "../api/claimHunter";
import { LoginPrompt } from "../components/LoginPrompt";
import { QueryFeedback } from "../components/QueryFeedback";
import type {
  AnswerDistribution,
  AnswerGroup,
  ClaimSummarySource,
  ClaimSupportView,
  EvidenceItem,
  EvidenceMode,
  JudgeModel,
  Population,
  StudyDesign,
} from "../types";
import { CITE_LABELS, formatCitation, type CiteFormat } from "../utils/citation";
import { loginUrl, PAGE_BASES, useEvidenceBase } from "../utils/pageBase";
import { markEvidenceSignupSource } from "../utils/signupSource";

/**
 * Open-question mode (auto triage, answer lists and answer groups) is stashed. While false the
 * page only sends claims and none of the question UI or copy is shown; the backend also forces
 * claim mode unless EVIDENCE_QUESTION_MODE is set.
 */
const QUESTION_MODE_ENABLED = false;

/**
 * Deprecated: the Author h-index sort, the per-paper "top author h-index" and its docs are hidden
 * while false. The API still carries `author_h_index` (null unless the evidence service fills it).
 */
const AUTHOR_H_INDEX_ENABLED = false;

/** Free-text search rules ("Add rules") are a staging preview; only the staging evidence service applies them. */
const RULES_ENABLED = IS_STAGING;

const RULES_PLACEHOLDER =
  "If you have a specific rule you'd like to apply to the search, feel free to spell it out here! " +
  "For example: don't include papers from XYZ journals; only include primary studies, not meta-analyses.";

/** Choosing the judge model is a staging preview; only the staging evidence service has both judges. */
const JUDGE_TOGGLE_ENABLED = IS_STAGING;

const JUDGE_LABELS: Record<JudgeModel, string> = { e2: "0.8B (E2)", r1: "2B (R1)" };

/** Prod summaries run on the self-hosted Qwen pod (CLAIM_SUMMARY_BASE_URL); staging is on Gemini Flash via OpenRouter for now. */
const SUMMARIES_IN_HOUSE = !IS_STAGING;

/** The claim tabs, or "answer" for the list of answers to an open question. */
type Side = "for" | "against" | "answer";
type SortKey = "weight" | "support" | "citations" | "author_h" | "date";

const SORT_LABELS: Record<SortKey, string> = {
  weight: "Suggested order",
  citations: "Citations",
  author_h: "Author h-index",
  support: "Support",
  date: "Date",
};

const SORT_KEYS = (Object.keys(SORT_LABELS) as SortKey[]).filter(
  (k) => AUTHOR_H_INDEX_ENABLED || k !== "author_h",
);

const sortLabel = (key: SortKey, side: Side): string =>
  side === "answer" && key === "support" ? "Confidence" : SORT_LABELS[key];

const DESIGN_LABELS: Record<StudyDesign, string | null> = {
  rct: "RCT",
  meta_analysis: "Meta-analysis",
  cohort: "Cohort",
  case_control: "Case-control",
  cross_sectional: "Cross-sectional",
  ecological: "Ecological",
  case_report: "Case report",
  surveillance: "Passive surveillance",
  in_vitro: "In vitro",
  animal: "Animal study",
  review: "Review",
  opinion: "Opinion / letter",
  other: null,
};

/** Designs that rarely establish causation on their own; their badge is highlighted. */
const WEAK_DESIGNS = new Set<StudyDesign>([
  "ecological",
  "surveillance",
  "case_report",
  "in_vitro",
  "animal",
  "opinion",
]);

const designLabel = (m: EvidenceItem): string | null =>
  m.design ? (DESIGN_LABELS[m.design] ?? null) : null;

/** Set once a logged-out visitor's free query has run (or the server says it's used up). */
const ANON_USED_KEY = "paperclip.evidence-api.anon-used";

const readAnonUsed = (): boolean => {
  try {
    return window.localStorage.getItem(ANON_USED_KEY) === "1";
  } catch {
    return false;
  }
};

/** Confidence floors the page offers; the API accepts 0.5–0.99. */
const CONF_STEPS = ["0.5", "0.6", "0.7", "0.8", "0.9"];

/** The steps, plus a saved permalink's off-step value so the dropdown can show it. */
const confOptions = (current: string): string[] =>
  CONF_STEPS.includes(current)
    ? CONF_STEPS
    : [...CONF_STEPS, current].sort((a, b) => Number(a) - Number(b));

/** Paths that render this page when loaded directly. */
const PAGE_PATHS = new Set<string>([...PAGE_BASES, "/claim-hunter"]);

const permalinkUrl = (base: string, id: string): string => `${window.location.origin}${base}/r/${id}`;

/** document.referrer when the browser landed on this page; this site after in-app navigation. */
const visitReferrer = (): string | null => {
  let landing = window.location.pathname;
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav) landing = new URL(nav.name).pathname;
  } catch {
    /* keep the current path */
  }
  const path = landing.replace(/\/+$/, "");
  return PAGE_PATHS.has(path) || PAGE_BASES.some((base) => path.startsWith(`${base}/r/`))
    ? document.referrer || null
    : window.location.origin;
};

const markAnonUsed = () => {
  try {
    window.localStorage.setItem(ANON_USED_KEY, "1");
  } catch {
    /* private mode: the server limit still applies */
  }
};

/** Passages retrieved per source: a new claim starts at the first step; each "widen" moves to the next. */
/** The first search's pool matches `paperclip evidence` (150; `--deep` = 500). */
const NET_STEPS = [150, 500, 1000];
/** The API's maximum: the page lists every passage it gets, grouped by article. */
const RESULT_LIMIT = 200;

/** The model's confidence for the side, or that the passage answers the question. */
const conf = (m: EvidenceItem, side: Side): number =>
  (side === "answer"
    ? m.confidence
    : side === "for"
      ? m.supports_score
      : m.refutes_score) ?? 0;

const sideItems = (d: ClaimSupportView, side: Side): EvidenceItem[] =>
  side === "answer" ? (d.answers ?? []) : side === "for" ? d.supports : d.refutes;

function Verdict({ m, side }: { m: EvidenceItem; side: Side }) {
  if (side === "answer")
    return <span className="answer">answers · {conf(m, side).toFixed(2)}</span>;
  return <span className={side}>{side === "for" ? "supports" : "refutes"}</span>;
}

const pubTime = (m: EvidenceItem): number => {
  const t = m.pub_date ? Date.parse(m.pub_date) : NaN;
  if (!Number.isNaN(t)) return t;
  return m.pub_year ? Date.UTC(m.pub_year, 0, 1) : 0;
};

const sortValue = (m: EvidenceItem, side: Side, key: SortKey): number => {
  switch (key) {
    case "weight":
      return m.evidence_weight ?? conf(m, side);
    case "support":
      return conf(m, side);
    case "citations":
      return m.citations ?? -1;
    case "author_h":
      return m.author_h_index ?? -1;
    case "date":
      return pubTime(m);
  }
};

/** One key per article: passages from the same paper share it. */
const articleKey = (m: EvidenceItem): string => m.doc_id ?? m.snippet.slice(0, 40);

const articleCount = (items: EvidenceItem[], withRetracted = true): number =>
  new Set(items.filter((m) => withRetracted || !m.retracted).map(articleKey)).size;

const ARXIV_ID = /^(\d{4}\.\d{4,5}(v\d+)?|[a-z-]+(\.[A-Z]{2})?\/\d{7})$/;

/** Canonical link to the full article, or null when the item has no id we can link. */
const articleUrl = (m: EvidenceItem): string | null => {
  const id = m.doc_id ?? "";
  if (/^PMC\d+$/.test(id)) return `https://www.ncbi.nlm.nih.gov/pmc/articles/${id}/`;
  if (m.source === "arxiv" || ARXIV_ID.test(id)) return `https://arxiv.org/abs/${id}`;
  if (m.doi) return `https://doi.org/${m.doi}`;
  if (m.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${m.pmid}/`;
  return null;
};

export const CSS = `
.ch-root {
  --ch-paper: var(--color-white, #fff); --ch-ink: var(--color-zinc-900, #1a1815);
  --ch-ink2: var(--color-zinc-600, #57534a); --ch-ink3: var(--color-zinc-500, #8a857a);
  --ch-rule: var(--color-zinc-200, #e4e0d8); --ch-rule2: var(--color-zinc-300, #c9c2b4);
  --ch-for: var(--color-emerald-700, #2f6b4f); --ch-against: var(--color-red-700, #8c3b2e);
  --ch-answer: var(--color-sky-800, #2b5675);
  background: var(--ch-paper); color: var(--ch-ink);
  flex: 1; min-height: 0; overflow-y: auto;
  font-family: "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif;
  font-size: 15px; line-height: 1.55;
}
.ch-wrap { max-width: 720px; margin: 0 auto; padding: 64px 24px 120px; }
.ch-kicker { font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ch-ink3); margin: 0 0 6px; font-weight: 400; }
.ch-lede { font-size: 18.5px; margin: 0 0 6px; font-style: italic; }
.ch-form { padding: 0; }
.ch-claim { width: 100%; border: none; border-bottom: 1px solid var(--ch-ink); outline: none; resize: none; background: transparent; font: inherit; font-size: 17.5px; line-height: 1.4; color: var(--ch-ink); padding: 0 0 8px; overflow: hidden; display: block; }
.ch-claim::placeholder { color: var(--ch-ink3); font-style: italic; }
.ch-bar { display: flex; align-items: baseline; justify-content: space-between; margin-top: 10px; }
.ch-rules-toggle { margin-top: 12px; font: inherit; font-size: 12px; letter-spacing: 0.04em; color: var(--ch-ink3); background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-rules { width: 100%; margin-top: 8px; border: 1px solid var(--ch-rule); outline: none; resize: vertical; background: transparent; font: inherit; font-size: 14px; line-height: 1.45; color: var(--ch-ink); padding: 8px 10px; display: block; }
.ch-rules:focus-visible { border-color: var(--ch-ink2); }
.ch-rules-used, .ch-judge-used { margin: 18px 0 0; font-size: 13px; color: var(--ch-ink2); }
.ch-rules-used b, .ch-judge-used b { font-weight: 500; letter-spacing: 0.04em; }
.ch-opts { font-size: 12px; color: var(--ch-ink3); letter-spacing: 0.04em; }
.ch-opts select { font: inherit; border: none; border-bottom: 1px solid var(--ch-rule); color: var(--ch-ink2); background: transparent; cursor: pointer; }
.ch-opts select:focus-visible { outline: 1px solid var(--ch-ink2); outline-offset: 2px; }
.ch-opts input { width: 52px; font: inherit; border: none; border-bottom: 1px solid var(--ch-rule); text-align: center; color: var(--ch-ink2); outline: none; background: transparent; }
.ch-go { font-family: inherit; font-size: 12.5px; letter-spacing: 0.18em; text-transform: uppercase; background: var(--ch-ink); color: var(--ch-paper); border: none; padding: 10px 26px; cursor: pointer; }
.ch-go:disabled { background: var(--ch-ink3); cursor: wait; }
.ch-go.locked:disabled { background: var(--ch-rule2); color: var(--ch-ink3); cursor: not-allowed; }
.ch-locked { margin-top: 8px; text-align: right; }
.ch-bar-left { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 20px; }
.ch-nolog { position: relative; display: inline-flex; outline: none; }
.ch-nolog .ch-toggle:has(input:disabled) { cursor: not-allowed; }
.ch-nolog:focus-visible .ch-toggle { outline: 1px solid var(--ch-ink2); outline-offset: 3px; }
.ch-nolog-tip { display: none; position: absolute; top: 100%; left: 0; padding-top: 6px; z-index: 10; }
.ch-nolog:hover .ch-nolog-tip, .ch-nolog:focus-within .ch-nolog-tip { display: block; }
.ch-nolog-tip span { display: block; white-space: nowrap; font-size: 12px; font-style: italic; color: var(--ch-ink2); background: var(--ch-paper); border: 1px solid var(--ch-rule2); padding: 4px 10px; }
.ch-nolog-tip a { color: var(--ch-ink); font-style: normal; text-decoration: underline; text-underline-offset: 3px; }
.ch-status { margin: 28px 0 0; font-style: italic; color: var(--ch-ink2); font-size: 14.5px; }
.ch-controls { display: flex; justify-content: space-between; align-items: baseline; margin: 40px 0 4px; flex-wrap: wrap; gap: 12px; }
.ch-tabs { display: flex; gap: 22px; }
.ch-tab { font-size: 12px; letter-spacing: 0.2em; text-transform: uppercase; font-weight: 600; background: none; border: none; padding: 0 0 4px; cursor: pointer; color: var(--ch-ink3); border-bottom: 2px solid transparent; font-family: inherit; }
.ch-tab.on.for { color: var(--ch-for); border-bottom-color: var(--ch-for); }
.ch-tab.on.against { color: var(--ch-against); border-bottom-color: var(--ch-against); }
.ch-tab.on.answer { color: var(--ch-answer); border-bottom-color: var(--ch-answer); cursor: default; }
.ch-sorter { font-size: 12px; color: var(--ch-ink3); letter-spacing: 0.05em; }
.ch-sorter select { font-family: inherit; font-size: 12px; color: var(--ch-ink2); border: none; border-bottom: 1px solid var(--ch-rule); background: transparent; outline: none; cursor: pointer; }
.ch-card { padding: 20px 0 18px; border-bottom: 1px solid var(--ch-rule); }
.ch-title { font-size: 15.5px; font-weight: 600; line-height: 1.35; margin-bottom: 3px; }
.ch-title a { color: inherit; text-decoration: none; border-bottom: 1px solid var(--ch-rule); }
.ch-title a:hover { border-bottom-color: var(--ch-ink2); }
.ch-right { display: flex; align-items: baseline; gap: 16px; }
.ch-time { font-size: 12px; color: var(--ch-ink3); letter-spacing: 0.05em; }
.ch-byline { font-size: 12.5px; color: var(--ch-ink2); margin-bottom: 3px; }
.ch-meta.ch-ids { margin: 0 0 10px; }
.ch-text { font-size: 14.5px; }
.ch-text.clamped { display: -webkit-box; -webkit-line-clamp: 5; -webkit-box-orient: vertical; overflow: hidden; }
.ch-more { font: inherit; font-size: 12px; font-style: italic; color: var(--ch-ink2); background: none; border: none; padding: 0; margin-top: 2px; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-sect { font-style: italic; color: var(--ch-ink2); }
.ch-figures { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 12px; }
.ch-figure { margin: 0; max-width: 340px; }
.ch-figure img { display: block; max-width: 100%; max-height: 260px; border: 1px solid var(--ch-rule); background: #fff; }
.ch-figure figcaption { font-size: 13px; line-height: 1.4; color: var(--ch-ink2); margin-top: 6px; }
.ch-figure-rel { color: var(--ch-ink3); }
.ch-meta { font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ch-ink3); margin-top: 10px; display: flex; gap: 14px; flex-wrap: wrap; }
.ch-meta a { color: var(--ch-ink2); text-decoration: none; border-bottom: 1px solid var(--ch-rule); }
.ch-cite { font: inherit; letter-spacing: inherit; text-transform: inherit; color: var(--ch-ink2); background: none; border: none; border-bottom: 1px solid var(--ch-rule); padding: 0; cursor: pointer; }
.ch-cite:hover, .ch-cite[data-state="open"] { color: var(--ch-ink); border-bottom-color: var(--ch-ink2); }
.ch-meta .for { color: var(--ch-for); }
.ch-meta .against { color: var(--ch-against); }
.ch-meta .answer { color: var(--ch-answer); }
.ch-answer { font-size: 16px; line-height: 1.45; margin: 4px 0 10px; padding-left: 12px; border-left: 3px solid var(--ch-answer); }
.ch-mode { margin-top: 18px; font-size: 13px; font-style: italic; color: var(--ch-ink2); }
.ch-mode button { font: inherit; font-style: normal; color: var(--ch-ink); background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-mode + .ch-widen { margin-top: 8px; }
.ch-faq h2 { font-size: 13px; font-weight: 600; margin: 18px 0 0; color: var(--ch-ink); }
.ch-faq ul { margin: 6px 0 0; padding-left: 18px; max-width: 620px; list-style: disc; }
.ch-faq li { margin: 3px 0 0; display: list-item; }
.ch-docs a { color: var(--ch-ink); text-decoration: underline; text-underline-offset: 3px; }
.ch-widen { margin-top: 32px; font-size: 13px; font-style: italic; color: var(--ch-ink2); }
.ch-widen + .ch-controls, .ch-summary-wrap + .ch-controls { margin-top: 14px; }
.ch-summary-wrap { margin-top: 18px; }
.ch-widen + .ch-widen { margin-top: 8px; }
.ch-widen + .ch-summary-wrap { margin-top: 14px; }
.ch-summary { border: 1px solid var(--ch-rule); padding: 12px 16px; font-size: 14.5px; line-height: 1.6; }
.ch-summary p { margin: 0 0 10px; }
.ch-summary p:last-child { margin-bottom: 0; }
.ch-summary .ch-wait { font-style: italic; color: var(--ch-ink3); }
.ch-ref { font: inherit; font-size: 0.82em; vertical-align: 0.25em; color: var(--ch-ink2); background: none; border: none; padding: 0 1px; cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
.ch-ref.for { color: var(--ch-for); }
.ch-ref.against { color: var(--ch-against); }
.ch-ref.answer { color: var(--ch-answer); }
.ch-refno { font-weight: 600; }
.ch-card.flash { animation: ch-flash 1.8s ease-out; }
@keyframes ch-flash { from { background: rgba(201, 194, 180, 0.45); } to { background: transparent; } }
.ch-widen button { font: inherit; font-style: normal; color: var(--ch-ink); background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-none { font-style: italic; color: var(--ch-ink3); padding: 18px 0; }
.ch-details { margin-top: 12px; }
.ch-details summary {
  cursor: pointer; font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--ch-ink2); list-style: none; border-bottom: 1px dotted var(--ch-rule2); display: inline-block;
}
.ch-details summary::-webkit-details-marker { display: none; }
.ch-details[open] summary { color: var(--ch-ink); }
.ch-passage { margin-top: 14px; padding-left: 14px; border-left: 2px solid var(--ch-rule); }
.ch-passage .ch-text { font-size: 14px; }
.ch-passage .ch-meta { margin-top: 6px; }
.ch-source { margin: 0 0 14px; font-size: 11.5px; color: var(--ch-ink3); letter-spacing: 0.06em; }
.ch-about { margin-top: 16px; }
.ch-results .ch-about { margin-top: 36px; }
.ch-docs { margin-top: 12px; font-size: 13px; color: var(--ch-ink2); }
.ch-copy-agent { font-family: inherit; font-size: 11.5px; letter-spacing: 0.12em; text-transform: uppercase; background: transparent; color: var(--ch-ink); border: 1px solid var(--ch-ink); padding: 6px 14px; cursor: pointer; }
.ch-copy-agent:hover { background: var(--ch-ink); color: var(--ch-paper); }
.ch-copy-row { margin: 12px 0 0; }
.ch-summarize-row { margin-top: 24px; }
.ch-widen + .ch-summarize-row { margin-top: 14px; }
.ch-summarize-row + .ch-controls { margin-top: 14px; }
.ch-about-links { font-size: 11.5px; letter-spacing: 0.06em; color: var(--ch-ink2); }
.ch-about-links button { font: inherit; letter-spacing: inherit; color: inherit; background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-about-links button[aria-expanded="true"] { color: var(--ch-ink); }
.ch-about-links button:focus-visible { outline: 1px solid var(--ch-ink2); outline-offset: 2px; }
.ch-about-links .ch-dot { margin: 0 8px; }
.ch-docs p { margin: 14px 0 0; }
.ch-faq { margin: 0; }
.ch-faq p { margin: 8px 0 0; max-width: 620px; }
.ch-docs pre { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; line-height: 1.5; overflow-x: auto; margin: 8px 0 0; color: var(--ch-ink); }
.ch-docs code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; color: var(--ch-ink); }
.ch-badge { display: inline-block; font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; padding: 1px 6px; border: 1px solid var(--ch-rule2); border-radius: 3px; color: var(--ch-ink2); line-height: 1.5; }
.ch-badge.weak { border-color: #c9a13b; color: #7a5a0c; background: rgba(201, 161, 59, 0.08); }
.ch-badge.retracted { border-color: #b42318; color: #fff; background: #b42318; font-weight: 600; text-decoration: none; }
.ch-badge.concern { border-color: #b54708; color: #b54708; }
.ch-also { margin-top: 6px; }
a.ch-badge.retracted:hover { background: #912018; }
.ch-badges { display: flex; gap: 6px; flex-wrap: wrap; margin: 2px 0 6px; }
.ch-card.retracted .ch-title, .ch-card.retracted .ch-text { opacity: 0.6; }
.ch-retracted-rule { margin-top: 26px; font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase; color: #b42318; border-bottom: 1px solid var(--ch-rule); padding-bottom: 4px; }
.ch-toggle { font-size: 12px; color: var(--ch-ink3); letter-spacing: 0.04em; display: inline-flex; align-items: center; gap: 4px; cursor: pointer; }
.ch-ev { padding: 1px 2px; border-radius: 2px; color: inherit; }
.ch-ev.for { background: rgba(47, 107, 79, 0.14); box-shadow: inset 0 -2px 0 rgba(47, 107, 79, 0.45); }
.ch-ev.against { background: rgba(140, 59, 46, 0.12); box-shadow: inset 0 -2px 0 rgba(140, 59, 46, 0.45); }
.ch-ev.answer { background: rgba(43, 86, 117, 0.12); box-shadow: inset 0 -2px 0 rgba(43, 86, 117, 0.45); }
.ch-group { padding: 22px 0 18px; border-bottom: 1px solid var(--ch-rule); }
.ch-glabel { font-size: 21px; line-height: 1.35; margin: 0 0 6px; }
.ch-glabel::before { content: "“"; color: var(--ch-ink3); }
.ch-glabel::after { content: "”"; color: var(--ch-ink3); }
.ch-gsum { font-size: 13px; color: var(--ch-ink2); letter-spacing: 0.02em; }
.ch-group .ch-badges { margin: 8px 0 0; }
.ch-group > .ch-details { margin-top: 10px; }
.ch-group .ch-card:last-child { border-bottom: none; }
.ch-other { margin-top: 26px; }
.ch-chips { display: flex; gap: 8px; flex-wrap: wrap; margin: 16px 0 0; font-size: 12px; }
.ch-chip { font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 11px; letter-spacing: 0.04em; padding: 2px 10px; border: 1px solid var(--ch-rule2); border-radius: 999px; background: none; color: var(--ch-ink2); cursor: pointer; }
.ch-chip.on { background: var(--ch-ink); border-color: var(--ch-ink); color: var(--ch-paper); }
.ch-view { font: inherit; font-size: 12px; color: var(--ch-ink2); background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.ch-plot { margin: 22px 0 4px; }
.ch-plot svg { display: block; width: 100%; height: auto; overflow: visible; }
.ch-plot text { font-family: var(--font-ui, "Inter", system-ui, sans-serif); font-size: 10px; fill: var(--ch-ink3); }
.ch-plot .ch-plot-title { font-size: 12.5px; color: var(--ch-ink2); margin-bottom: 4px; }
.ch-plot .ch-plot-key { display: flex; gap: 14px; font-size: 11.5px; color: var(--ch-ink3); margin-top: 2px; }
.ch-plot .ch-plot-key i { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px; vertical-align: 0; }
`;

/** Self-contained Markdown brief an AI agent can follow to call the Evidence API. */
function agentBrief(endpoint: string): string {
  return `# GXL Evidence API

Finds published evidence that supports or refutes a scientific claim. Each claim is run against 360M+ passages across PMC, arXiv, bioRxiv & medRxiv, and abstracts; every retrieved passage is read by GXL's trained model, which marks the exact sentences that support or refute the claim.

## Request

POST ${endpoint}
Headers:
- Authorization: Bearer $PAPERCLIP_API_KEY  (create a key at ${endpoint.replace(/\/api\/v1\/claims\/support$/, "")}/settings/keys; the secret is shown once)
- Content-Type: application/json

Body (only \`claim\` is required):
- claim: string, 3-590 chars. One specific, checkable scientific claim (e.g. "Metformin improves survival in pancreatic cancer"), not a question.
- limit: int 1-200, default 25. Max items returned per list.
- min_conf: float 0.5-0.99, default 0.8. Confidence floor for a passage to count as evidence.
- top_n: int 1-1000, default 150. Passages retrieved per source before judging (more = slower, broader).
- hybrid: bool, default true. Add keyword matches to the semantic search.
- include_context: bool, default false. Also return each full passage as \`context\` plus \`evidence_offset\` [start, end].
- include_neutral: bool, default false. Also return \`neutral\`: passages judged neither way.

Example:
curl -X POST ${endpoint} \\
  -H "Authorization: Bearer $PAPERCLIP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"claim": "Metformin improves survival in pancreatic cancer"}'

## Response (JSON)

{
  "id": "req_...", "object": "claim_evidence", "claim": "...", "model_version": "gxl-evidence-2026-10",
  "counts": {"retrieved": 120, "supports": 52, "refutes": 30, "neutral": 38, "supports_retracted": 0, "refutes_retracted": 0},
  "supports": [Evidence], "refutes": [Evidence],
  "timing_ms": {"search": 118, "judge": 808, "total": 950}
}

Evidence:
{
  "evidence": "the sentences that support or refute the claim",
  "confidence": 0-1 (for its side), "weight": 0-1 (ranking score), "section": "DISCUSSION",
  "paper": {"id": "PMC... | preprint id | PMID:...", "source": "pmc", "title": "...", "journal": "...",
            "authors": ["First Author"], "authors_truncated": true, "doi": "...", "pmid": "...",
            "date": "YYYY-MM-DD", "year": 2017, "citations": 69,${AUTHOR_H_INDEX_ENABLED ? ` "author_h_index": 14,` : ""}
            "design": "rct | meta_analysis | cohort | case_control | cross_sectional | ecological | case_report | surveillance | in_vitro | animal | review | opinion | other | null",
            "retracted": false, "expression_of_concern": false, "retraction_doi": null, "retraction_notice": null}
}

## How to use the results

- \`supports\` and \`refutes\` are ordered best first by \`weight\` (confidence x study design x citations; retracted papers last) and cut to \`limit\`; \`counts\` has the totals before the cut.
- Passages are receipts, not rulings: weigh study design, population and recency yourself, and quote \`evidence\` with its paper (DOI or PMID) when citing.
- Treat \`retracted: true\` or \`expression_of_concern: true\` papers as unreliable.
- Every key is always present (null when unknown). The \`X-Request-Id\` response header equals \`id\`.
- Errors return JSON with an HTTP status: 400 invalid request, 401 bad or missing key, 429 rate limited (retry after a pause), 503 temporarily unavailable.
- Queries are not stored.
`;
}

function CopyForAgent() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(agentBrief(`${window.location.origin}/api/v1/claims/support`));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <p className="ch-copy-row">
      <button type="button" className="ch-copy-agent" onClick={() => void copy()}>
        {copied ? "Copied" : "Copy for your agent"}
      </button>
    </p>
  );
}

function ApiDocs() {
  // Set after mount: the server render has no window, and the first client render must match it.
  const [origin, setOrigin] = useState("");
  useEffect(() => setOrigin(window.location.origin), []);
  const endpoint = `${origin}/api/v1/claims/support`;
  return (
    <div className="ch-docs" id="ch-api-docs">
      <p>
        The Evidence API does not store queries sent with an API key, unless you ask for a
        permalink with the <code>X-Evidence-Permalink: 1</code> header. Then it saves the claim
        and the results, and anyone with the link can see them.
      </p>
      <p>
        You need a Paperclip API key.{" "}
        <Link to="/settings/keys">Get your API key</Link> (Settings &rarr; API keys): give the
        key a name and create it, then copy it right away, as the secret is shown only once.
        Send it as a bearer token:
      </p>
      <pre>{`curl -X POST ${endpoint} \\
  -H "Authorization: Bearer $PAPERCLIP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"claim": "Metformin improves survival in pancreatic cancer"}'`}</pre>
      <p>
        Optional: <code>limit</code> (items per list, 1&ndash;200, default 25),{" "}
        <code>min_conf</code> (0.5&ndash;0.99, default 0.8), <code>top_n</code> (passages per
        source, 1&ndash;1000, default 150), <code>hybrid</code> (add keyword matches, default
        true), <code>include_context</code> (default false: also return each full passage) and{" "}
        <code>include_neutral</code> (default false: also return passages judged neither way)
        {QUESTION_MODE_ENABLED ? (
          <>
            , <code>mode</code> (see below)
          </>
        ) : null}
        .
      </p>
      <pre>{`{
  "id": "req_3f9c1a7b2e8d4c6a0b1f",
  "object": "claim_evidence",
  "claim": "Metformin improves survival in pancreatic cancer",
  "model_version": "gxl-evidence-2026-10",
  "counts": {"retrieved": 120, "supports": 52, "refutes": 30, "neutral": 38,
             "supports_retracted": 0, "refutes_retracted": 0},
  "supports": [{
    "evidence": "the use of metformin is associated with improved OS ...",
    "confidence": 1.0,
    "weight": 0.8653,
    "section": "DISCUSSION",
    "paper": {
      "id": "PMC5421925", "source": "pmc",
      "title": "Metformin is associated with survival benefit in ...",
      "journal": "Oncotarget",
      "authors": ["Ping-Ting Zhou"], "authors_truncated": true,
      "doi": "10.18632/oncotarget.15692", "pmid": "28445955",
      "date": "2017-02-25", "year": 2017, "citations": 69,${AUTHOR_H_INDEX_ENABLED ? ` "author_h_index": 14,` : ""}
      "design": "meta_analysis",
      "retracted": false, "expression_of_concern": false,
      "retraction_doi": null, "retraction_notice": null
    }
  }],
  "refutes": [ ... ],
  "timing_ms": {"search": 118, "judge": 808, "total": 950}
}`}</pre>
      <p>
        <code>supports</code> and <code>refutes</code> are ordered best first by{" "}
        <code>weight</code> (confidence × study design × citations; retracted papers last) and cut
        to <code>limit</code>; <code>counts</code> has the totals before the cut. Each item has{" "}
        <code>evidence</code> (the sentences that support or refute the claim),{" "}
        <code>confidence</code> (0&ndash;1, for its side), <code>section</code> and{" "}
        <code>paper</code>. <code>paper.id</code> is a PMC id, a preprint id or{" "}
        <code>PMID:&hellip;</code>; <code>design</code> is e.g. <code>rct</code>,{" "}
        <code>meta_analysis</code>, <code>cohort</code>, <code>ecological</code>; retractions come
        from Retraction Watch. Every key is always present, <code>null</code> when unknown.
      </p>
      <p>
        With <code>include_context</code>, each item also has <code>context</code> (the full
        passage) and <code>evidence_offset</code> (<code>[start, end]</code> character offsets of{" "}
        <code>evidence</code> in it). With <code>include_neutral</code>, <code>neutral</code>{" "}
        lists the passages judged neither way, closest calls first; their{" "}
        <code>confidence</code> is for the side they lean to, given as <code>leans</code>.{" "}
        Every response has an <code>X-Request-Id</code> header equal to <code>id</code>.
      </p>
      {QUESTION_MODE_ENABLED ? (
        <>
          <p>
            <strong>Open questions.</strong> The same endpoint answers questions that ask what,
            how, why, which or when. With <code>mode</code> <code>&quot;auto&quot;</code> (the
            default) a quick model check decides whether the input is a claim or an open question;
            pass <code>&quot;claim&quot;</code> or <code>&quot;question&quot;</code> to choose.
            Yes/no questions (&ldquo;Does metformin extend lifespan?&rdquo;) are treated as claims.
            The response&rsquo;s <code>mode</code> says which path ran, and <code>triage</code>{" "}
            has the check&rsquo;s <code>confidence</code>.
          </p>
          <pre>{`curl -X POST ${endpoint} \\
      -H "Authorization: Bearer $PAPERCLIP_API_KEY" \\
      -H "Content-Type: application/json" \\
      -d '{"claim": "What is the mechanism of action of semaglutide?"}'`}</pre>
          <p>
            For a question, <code>supports</code>, <code>refutes</code> and{" "}
            <code>neutrals</code> are empty and <code>answers</code> lists the passages that answer
            it at or above <code>min_conf</code>, most confident first. Each answer has{" "}
            <code>answer</code> (the answering text), <code>answer_start</code>/
            <code>answer_end</code> (offsets into <code>snippet</code>, also given as{" "}
            <code>evidence</code>/<code>evidence_start</code>/<code>evidence_end</code>),{" "}
            <code>spans</code> (up to three answering spans in the passage, each with{" "}
            <code>text</code>, <code>start</code>, <code>end</code>, <code>confidence</code>),{" "}
            <code>confidence</code>, and the same paper, citation and credibility fields as
            evidence.
          </p>
          <p>
            Pass <code>&quot;group&quot;: true</code> to also get <code>groups</code>: answers that
            say the same thing, best first. Each group has <code>label</code> (the verbatim text of
            one answer, <code>label_answer_idx</code>), <code>members</code> (indexes into{" "}
            <code>answers</code>), <code>papers</code> (independent papers),{" "}
            <code>designs</code>, <code>years</code>, <code>population</code> (papers about{" "}
            <code>human</code>, <code>animal</code> or <code>in_vitro</code> results) and{" "}
            <code>score</code>. <code>other</code> lists the answers in no group, and for numeric
            questions <code>distribution</code> has every reported value in one unit (
            <code>unit</code>, <code>points</code> with <code>value</code>, <code>text</code>,{" "}
            <code>answer_idx</code>, <code>design</code>, <code>population</code>). Grouping adds
            about a third of a second.
          </p>
        </>
      ) : null}
    </div>
  );
}

/** Passages run to ~2,500 chars; ~5 lines fit in about this many. */
const CLAMP_CHARS = 480;

/** The evidence span, when the service returned one that fits the snippet. */
function evidenceRange(m: EvidenceItem): [number, number] | null {
  const s = m.evidence_start;
  const e = m.evidence_end;
  return s != null && e != null && s >= 0 && e > s && e <= m.snippet.length ? [s, e] : null;
}

/** Ranges to highlight: every answer span for an answer, else the evidence span. */
function highlightRanges(m: EvidenceItem): [number, number][] {
  const fromSpans = (m.spans ?? [])
    .map((s): [number, number] => [s.start, s.end])
    .filter(([s, e]) => s >= 0 && e > s && e <= m.snippet.length)
    .sort((a, b) => a[0] - b[0]);
  const ev = evidenceRange(m);
  const ranges = fromSpans.length ? fromSpans : ev ? [ev] : [];
  return ranges.filter((r, i) => i === 0 || r[0] >= ranges[i - 1][1]);
}

function PassageText({ m, side }: { m: EvidenceItem; side: Side }) {
  const [open, setOpen] = useState(false);
  const long = m.snippet.length > CLAMP_CHARS;
  const ranges = highlightRanges(m);
  const first = ranges[0]?.[0];
  // Collapsed, start a little before the first highlight so it is visible.
  let from = 0;
  if (first != null && long && !open && first > CLAMP_CHARS / 3) {
    const cut = m.snippet.lastIndexOf(". ", first - 1);
    from = cut >= 0 && first - cut < CLAMP_CHARS / 2 ? cut + 2 : first;
  }
  const parts: React.ReactNode[] = [];
  let at = from;
  for (const [s, e] of ranges) {
    parts.push(m.snippet.slice(at, s));
    parts.push(
      <mark key={s} className={`ch-ev ${side}`}>
        {m.snippet.slice(s, e)}
      </mark>,
    );
    at = e;
  }
  parts.push(m.snippet.slice(at));
  return (
    <>
      <div className={`ch-text${long && !open ? " clamped" : ""}`}>
        {m.section && from === 0 ? <span className="ch-sect">{m.section}. </span> : null}
        {from > 0 ? "… " : null}
        {parts}
      </div>
      {long ? (
        <button type="button" className="ch-more" onClick={() => setOpen(!open)}>
          {open ? "less" : "more"}
        </button>
      ) : null}
    </>
  );
}

function CiteMenu({ m }: { m: EvidenceItem }) {
  const [copied, setCopied] = useState<CiteFormat | null>(null);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async (format: CiteFormat) => {
    try {
      await navigator.clipboard.writeText(formatCitation(m, format));
      setCopied(format);
    } catch {
      setCopied(null);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ch-cite">
        {copied ? `copied ${CITE_LABELS[copied]}` : "cite"}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {(Object.keys(CITE_LABELS) as CiteFormat[]).map((f) => (
          <DropdownMenuItem key={f} onSelect={() => void copy(f)}>
            Copy {CITE_LABELS[f]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Figures({ m }: { m: EvidenceItem }) {
  const figures = (m.figures ?? []).filter((f) => f.image_url);
  if (!figures.length) return null;
  return (
    <div className="ch-figures">
      {figures.map((f) => (
        <figure key={f.path} className="ch-figure">
          <a href={f.image_url ?? undefined} target="_blank" rel="noreferrer">
            <img src={f.image_url ?? undefined} alt={f.label ?? "Figure"} loading="lazy" />
          </a>
          <figcaption>
            <b>{f.label ?? "Figure"}</b>
            <span className="ch-figure-rel">
              {f.relation === "caption" ? " · this passage is its caption" : " · cited in the passage"}
            </span>
            {f.caption ? <span className="ch-figure-cap"> {f.caption}</span> : null}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function Passage({ m, side }: { m: EvidenceItem; side: Side }) {
  return (
    <div className="ch-passage">
      <PassageText m={m} side={side} />
      <Figures m={m} />
      <div className="ch-meta">
        <Verdict m={m} side={side} />
      </div>
    </div>
  );
}

function Card({
  m,
  rest,
  side,
  refNo,
  alsoQuotedIn,
}: {
  m: EvidenceItem;
  rest?: EvidenceItem[];
  side: Side;
  refNo?: number;
  alsoQuotedIn?: EvidenceItem[];
}) {
  const inst = m.institutions?.[0]?.split(",")[0];
  const byline = [
    m.authors,
    m.journal ? <i key="j">{m.journal}</i> : null,
    inst,
    m.pub_year,
  ].filter(Boolean);
  const design = designLabel(m);
  return (
    <div
      className={`ch-card${m.retracted ? " retracted" : ""}`}
      id={refNo ? `ch-ref-${refNo}` : undefined}
    >
      {m.retracted || m.expression_of_concern || design ? (
        <div className="ch-badges">
          {m.retracted ? (
            m.retraction_doi ? (
              <a
                className="ch-badge retracted"
                href={`https://doi.org/${m.retraction_doi}`}
                target="_blank"
                rel="noreferrer"
                title={m.retraction_notice ?? "Retracted"}
              >
                Retracted
              </a>
            ) : (
              <span className="ch-badge retracted" title={m.retraction_notice ?? "Retracted"}>
                Retracted
              </span>
            )
          ) : null}
          {m.expression_of_concern ? (
            <span className="ch-badge concern" title={m.retraction_notice ?? undefined}>
              Expression of concern
            </span>
          ) : null}
          {design ? (
            <span
              className={`ch-badge${m.design && WEAK_DESIGNS.has(m.design) ? " weak" : ""}`}
              title="Study design (automatic classification)"
            >
              {design}
            </span>
          ) : null}
        </div>
      ) : null}
      {m.title ? (
        <div className="ch-title">
          {refNo ? <span className="ch-refno">[{refNo}] </span> : null}
          {articleUrl(m) ? (
            <a href={articleUrl(m) ?? undefined} target="_blank" rel="noreferrer">
              {m.title}
            </a>
          ) : (
            m.title
          )}
        </div>
      ) : null}
      {byline.length ? (
        <div className="ch-byline">
          {byline.map((part, i) => (
            <span key={i}>
              {i > 0 ? " · " : ""}
              {part}
            </span>
          ))}
        </div>
      ) : null}
      <div className="ch-meta ch-ids">
        {m.doc_id && /^PMC\d+$/.test(m.doc_id) ? (
          <a
            href={`https://www.ncbi.nlm.nih.gov/pmc/articles/${m.doc_id}/`}
            target="_blank"
            rel="noreferrer"
          >
            {m.doc_id}
          </a>
        ) : (
          <span>{m.doc_id}</span>
        )}
        {m.doi ? (
          <a href={`https://doi.org/${m.doi}`} target="_blank" rel="noreferrer">
            doi:{m.doi}
          </a>
        ) : null}
        <Verdict m={m} side={side} />
        {m.citations != null ? <span>{m.citations} citations</span> : null}
        {AUTHOR_H_INDEX_ENABLED && m.author_h_index != null ? (
          <span title="Highest h-index among the paper's authors">
            top author h-index {m.author_h_index}
          </span>
        ) : null}
        {m.title ? <CiteMenu m={m} /> : null}
      </div>
      {side === "answer" && m.answer ? <div className="ch-answer">{m.answer}</div> : null}
      <PassageText m={m} side={side} />
      <Figures m={m} />
      {alsoQuotedIn && alsoQuotedIn.length > 0 ? (
        <div className="ch-meta ch-also">
          Also quoted in:{" "}
          {alsoQuotedIn.map((o, i) => (
            <span key={i}>
              {i > 0 ? ", " : ""}
              {o.doc_id}
              {o.pub_year ? ` (${o.pub_year})` : ""}
            </span>
          ))}
        </div>
      ) : null}
      {rest && rest.length > 0 ? (
        <details className="ch-details">
          <summary>More from this paper</summary>
          {rest.map((p, i) => (
            <Passage key={i} m={p} side={side} />
          ))}
        </details>
      ) : null}
    </div>
  );
}

interface ArticleGroup {
  best: EvidenceItem;
  rest: EvidenceItem[];
  /** Other articles whose best passage quotes the identical span. */
  alsoQuotedIn?: EvidenceItem[];
}

const spanKey = (m: EvidenceItem): string =>
  (m.evidence ?? "").toLowerCase().replace(/\s+/g, " ").trim();

/** Articles quoting the identical span collapse onto the original (earliest published,
 * then most cited, never a retracted one if avoidable), as in `paperclip evidence`. */
function collapseQuotedSpans(groups: ArticleGroup[]): ArticleGroup[] {
  const byKey = new Map<string, ArticleGroup[]>();
  groups.forEach((g, i) => {
    const key = spanKey(g.best) || `#${i}`;
    const arr = byKey.get(key);
    if (arr) arr.push(g);
    else byKey.set(key, [g]);
  });
  return [...byKey.values()].map((members) => {
    if (members.length === 1) return members[0];
    const live = members.filter((g) => !g.best.retracted);
    const original = [...(live.length ? live : members)].sort(
      (a, b) =>
        (pubTime(a.best) || Infinity) - (pubTime(b.best) || Infinity) ||
        (b.best.citations ?? 0) - (a.best.citations ?? 0),
    )[0];
    return { ...original, alsoQuotedIn: members.filter((g) => g !== original).map((g) => g.best) };
  });
}

/** One entry per article, its strongest passage first, articles in the chosen order;
 * retracted articles always last. */
function groupArticles(items: EvidenceItem[], side: Side, sort: SortKey): ArticleGroup[] {
  const byDoc = new Map<string, EvidenceItem[]>();
  for (const m of items) {
    const key = articleKey(m);
    const arr = byDoc.get(key);
    if (arr) arr.push(m);
    else byDoc.set(key, [m]);
  }
  const grouped = [...byDoc.values()].map((passages) => {
    const sorted = [...passages].sort((a, b) => conf(b, side) - conf(a, side));
    return { best: sorted[0], rest: sorted.slice(1) };
  });
  const ordered = grouped.sort(
    (a, b) =>
      Number(!!a.best.retracted) - Number(!!b.best.retracted) ||
      sortValue(b.best, side, sort) - sortValue(a.best, side, sort),
  );
  const collapsed = collapseQuotedSpans(ordered);
  return [...collapsed.filter((g) => !g.best.retracted), ...collapsed.filter((g) => g.best.retracted)];
}

const POP_LABELS: Record<Exclude<Population, "unknown">, string> = {
  human: "People",
  animal: "Animals",
  in_vitro: "Lab (in vitro)",
};
const POP_COLORS: Record<Exclude<Population, "unknown">, string> = {
  human: "#2b5675",
  animal: "#b7791f",
  in_vitro: "#7d8b6a",
};
/** Population tags below this model confidence are not shown. */
const POP_MIN_P = 0.6;

type PopKey = Exclude<Population, "unknown">;

const popOf = (m: EvidenceItem): PopKey | null =>
  m.population && m.population !== "unknown" && (m.population_p ?? 0) >= POP_MIN_P
    ? m.population
    : null;

/** Papers per population, most first. */
function popCounts(items: EvidenceItem[]): [PopKey, number][] {
  const seen = new Map<PopKey, Set<string>>();
  for (const m of items) {
    const p = popOf(m);
    if (!p) continue;
    if (!seen.has(p)) seen.set(p, new Set());
    seen.get(p)?.add(articleKey(m));
  }
  return [...seen.entries()].map(([p, s]): [PopKey, number] => [p, s.size]).sort((a, b) => b[1] - a[1]);
}

/** Strongest designs first, for the group summary line. */
const DESIGN_ORDER: StudyDesign[] = [
  "meta_analysis",
  "rct",
  "cohort",
  "case_control",
  "cross_sectional",
  "review",
  "ecological",
  "case_report",
  "surveillance",
  "animal",
  "in_vitro",
  "opinion",
];

/** "7 papers · 2 RCT · 1 meta-analysis · 2009–2024" for a group's answers. */
function groupSummary(items: EvidenceItem[]): string {
  const papers = new Map<string, EvidenceItem>();
  for (const m of items) if (!papers.has(articleKey(m))) papers.set(articleKey(m), m);
  const counts = new Map<StudyDesign, number>();
  for (const m of papers.values()) {
    if (m.design && DESIGN_LABELS[m.design]) counts.set(m.design, (counts.get(m.design) ?? 0) + 1);
  }
  const designs = DESIGN_ORDER.filter((d) => counts.has(d))
    .slice(0, 3)
    .map((d) => `${counts.get(d)} ${d === "rct" ? "RCT" : (DESIGN_LABELS[d] ?? "").toLowerCase()}`);
  const years = [...papers.values()].map((m) => m.pub_year).filter((y): y is number => !!y);
  const lo = Math.min(...years);
  const hi = Math.max(...years);
  const span = years.length ? (lo === hi ? `${lo}` : `${lo}–${hi}`) : null;
  const n = papers.size;
  return [`${n} ${n === 1 ? "paper" : "papers"}`, ...designs, span].filter(Boolean).join(" · ");
}

function AnswerGroupView({
  label,
  items,
  sort,
  refNumbers,
}: {
  label: string;
  items: EvidenceItem[];
  sort: SortKey;
  refNumbers: Map<string, number>;
}) {
  const articles = groupArticles(items, "answer", sort);
  const pops = popCounts(items);
  return (
    <section className="ch-group">
      <div className="ch-glabel">{label}</div>
      <div className="ch-gsum">{groupSummary(items)}</div>
      {pops.length ? (
        <div className="ch-badges">
          {pops.map(([p, n]) => (
            <span key={p} className="ch-badge" title="Who or what the results are about">
              {POP_LABELS[p]} · {n}
            </span>
          ))}
        </div>
      ) : null}
      <details className="ch-details">
        <summary>
          {articles.length === 1 ? "Show the answer" : `Show all ${articles.length} answers`}
        </summary>
        {articles.map((a, i) => (
          <Card
            key={i}
            m={a.best}
            rest={a.rest}
            side="answer"
            refNo={refNumbers.get(`answer|${articleKey(a.best)}`)}
          />
        ))}
      </details>
    </section>
  );
}

const sig = (v: number): number => Number(v.toPrecision(v >= 100 ? 3 : 2));

/** A value in the distribution's unit, durations in the most readable unit. */
function fmtValue(v: number, unit: string): string {
  if (unit !== "h") return `${sig(v)} ${unit}`;
  if (v < 1) return `${sig(v * 60)} min`;
  if (v < 48) return `${sig(v)} h`;
  if (v < 24 * 90) return `${sig(v / 24)} d`;
  if (v < 8766 * 2) return `${sig(v / 730.5)} mo`;
  return `${sig(v / 8766)} y`;
}

/** Dot plot of the values numeric answers report, one dot per answer, stacked where they meet. */
function DotPlot({ dist, answers, pop }: { dist: AnswerDistribution; answers: EvidenceItem[]; pop: PopKey | null }) {
  const pts = dist.points.filter((p) => p.value > 0 && (!pop || popOf(answers[p.answer_idx]) === pop));
  if (pts.length < 3) return null;
  const vals = pts.map((p) => p.value).sort((a, b) => a - b);
  const lo = vals[0];
  const hi = vals[vals.length - 1];
  const log = hi / lo > 20;
  const W = 640;
  const L = 16;
  const R = W - 16;
  const span = (f: (v: number) => number) => (v: number) =>
    hi === lo ? (L + R) / 2 : L + ((f(v) - f(lo)) / (f(hi) - f(lo))) * (R - L);
  const x = span(log ? Math.log10 : (v) => v);
  const R0 = 4.5;
  const stack = new Map<number, number>();
  const dots = pts.map((p) => {
    const cx = x(p.value);
    const bin = Math.round(cx / (2 * R0 + 1));
    const k = stack.get(bin) ?? 0;
    stack.set(bin, k + 1);
    return { p, cx, k };
  });
  const rows = Math.max(...dots.map((d) => d.k)) + 1;
  const base = 12 + rows * (2 * R0 + 1);
  const H = base + 26;
  const ticks: number[] = [];
  if (log) {
    for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e++)
      for (const m of [1, 3]) {
        const t = m * 10 ** e;
        if (t >= lo && t <= hi) ticks.push(t);
      }
  } else {
    const step = 10 ** Math.floor(Math.log10((hi - lo) / 4 || 1));
    const nice = [1, 2, 5, 10].map((m) => m * step).find((s) => (hi - lo) / s <= 6) ?? 10 * step;
    for (let t = Math.ceil(lo / nice) * nice; t <= hi; t += nice) ticks.push(t);
  }
  const median = vals[Math.floor(vals.length / 2)];
  const present = [...new Set(pts.map((p) => popOf(answers[p.answer_idx])))];
  return (
    <div className="ch-plot">
      <div className="ch-plot-title">
        {pts.length} reported values · median {fmtValue(median, dist.unit)}
        {log ? " · log scale" : ""}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Reported values">
        <line x1={L} x2={R} y1={base + 4} y2={base + 4} stroke="var(--ch-rule2)" />
        <line x1={x(median)} x2={x(median)} y1={4} y2={base + 4} stroke="var(--ch-ink3)" strokeDasharray="2 3" />
        {ticks.map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={base + 4} y2={base + 8} stroke="var(--ch-rule2)" />
            <text x={x(t)} y={base + 20} textAnchor="middle">
              {fmtValue(t, dist.unit)}
            </text>
          </g>
        ))}
        {dots.map(({ p, cx, k }, i) => {
          const pp = popOf(answers[p.answer_idx]);
          const m = answers[p.answer_idx];
          return (
            <circle
              key={i}
              cx={cx}
              cy={base - R0 - k * (2 * R0 + 1)}
              r={R0}
              fill={pp ? POP_COLORS[pp] : "var(--ch-paper)"}
              stroke={pp ? POP_COLORS[pp] : "var(--ch-ink3)"}
              fillOpacity={0.85}
            >
              <title>
                {[p.text, m.title, designLabel(m), m.pub_year].filter(Boolean).join(" · ")}
              </title>
            </circle>
          );
        })}
      </svg>
      <div className="ch-plot-key">
        {present.map((pp) => (
          <span key={pp ?? "none"}>
            <i
              style={{
                background: pp ? POP_COLORS[pp] : "transparent",
                border: `1px solid ${pp ? POP_COLORS[pp] : "var(--ch-ink3)"}`,
              }}
            />
            {pp ? POP_LABELS[pp] : "Not stated"}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Articles per side sent to the summarizer. */
const SUMMARY_PER_SIDE = 40;
/** Context kept around the evidence span, in characters. */
const SUMMARY_CONTEXT = 350;

function sourceText(m: EvidenceItem): string {
  const ev = evidenceRange(m);
  if (!ev) return m.snippet.slice(0, 2 * SUMMARY_CONTEXT);
  const from = Math.max(0, ev[0] - SUMMARY_CONTEXT);
  const to = Math.min(m.snippet.length, ev[1] + SUMMARY_CONTEXT);
  return `${from > 0 ? "… " : ""}${m.snippet.slice(from, to)}${to < m.snippet.length ? " …" : ""}`;
}

interface SummaryRef {
  side: Side;
  key: string;
}

interface SummaryState {
  text: string;
  streaming: boolean;
  error: string | null;
  refs: Map<number, SummaryRef>;
}

/** "[3]", "[2, 7]", "[4-6]" → the numbers cited. */
function citedNumbers(inner: string): number[] {
  const out: number[] = [];
  for (const part of inner.split(/\s*,\s*/)) {
    const range = part.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (range) {
      const [a, b] = [Number(range[1]), Number(range[2])];
      for (let n = a; n <= b && n - a < 20; n++) out.push(n);
    } else if (/^\d+$/.test(part)) out.push(Number(part));
  }
  return out;
}

function SummaryText({
  summary,
  onCite,
}: {
  summary: SummaryState;
  onCite: (n: number) => void;
}) {
  const renderLine = (line: string, key: number) => {
    const parts: React.ReactNode[] = [];
    let last = 0;
    for (const match of line.matchAll(/\[(\d+(?:\s*[,–-]\s*\d+)*)\]/g)) {
      const nums = citedNumbers(match[1]).filter((n) => summary.refs.has(n));
      if (!nums.length) continue;
      parts.push(line.slice(last, match.index));
      for (const n of nums) {
        const ref = summary.refs.get(n) as SummaryRef;
        parts.push(
          <button
            key={`${match.index}-${n}`}
            type="button"
            className={`ch-ref ${ref.side}`}
            onClick={() => onCite(n)}
            title={
              QUESTION_MODE_ENABLED && ref.side === "answer"
                ? "Answering source"
                : ref.side === "for"
                  ? "Supporting source"
                  : "Refuting source"
            }
          >
            [{n}]
          </button>,
        );
      }
      last = (match.index ?? 0) + match[0].length;
    }
    parts.push(line.slice(last));
    return <p key={key}>{parts}</p>;
  };
  const blocks = summary.text.split(/\n+/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="ch-summary" aria-live="polite">
      {summary.text ? (
        blocks.map(renderLine)
      ) : summary.error ? (
        <p className="ch-wait">{summary.error}</p>
      ) : (
        <p className="ch-wait">Reading the evidence…</p>
      )}
    </div>
  );
}

/** The passage with each highlighted span in bold, as a markdown blockquote. */
function quotedPassage(m: EvidenceItem): string {
  const bold = (s: string) =>
    s
      .split("\n")
      .map((l) => l.replace(/^(\s*)(.*?)(\s*)$/, (_, a, t, b) => (t ? `${a}**${t}**${b}` : l)))
      .join("\n");
  let text = "";
  let at = 0;
  for (const [s, e] of highlightRanges(m)) {
    text += m.snippet.slice(at, s) + bold(m.snippet.slice(s, e));
    at = e;
  }
  text += m.snippet.slice(at);
  return text
    .split(/\n+/)
    .map((l) => `> ${l.trim()}`)
    .join("\n>\n");
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const EXPORT_SIDE_TITLES: Record<Side, string> = {
  for: "Supporting evidence",
  against: "Refuting evidence",
  answer: "Answers",
};

function exportArticle(g: ArticleGroup, side: Side, n: string): string {
  const m = g.best;
  const ids = [
    m.doi ? `DOI [${m.doi}](https://doi.org/${m.doi})` : null,
    m.pmid ? `PMID [${m.pmid}](https://pubmed.ncbi.nlm.nih.gov/${m.pmid}/)` : null,
    m.doc_id && m.doc_id !== m.doi && m.doc_id !== m.pmid ? `ID ${m.doc_id}` : null,
  ].filter(Boolean);
  const url = articleUrl(m);
  const meta = [
    m.authors ? `Authors: ${m.authors}` : null,
    m.journal ? `Journal: ${m.journal}` : null,
    m.pub_date || m.pub_year ? `Published: ${m.pub_date ?? m.pub_year}` : null,
    designLabel(m) ? `Study design: ${designLabel(m)}` : null,
    m.institutions?.length ? `Institutions: ${m.institutions.slice(0, 3).join("; ")}` : null,
    m.citations != null ? `Citations: ${m.citations}` : null,
    AUTHOR_H_INDEX_ENABLED && m.author_h_index != null
      ? `Top author h-index: ${m.author_h_index}`
      : null,
    m.source ? `Source: ${m.source}` : null,
    ids.length ? ids.join(" · ") : null,
    url ? `Link: ${url}` : null,
    m.evidence_weight != null ? `Evidence weight: ${m.evidence_weight.toFixed(3)}` : null,
    m.retracted
      ? `**RETRACTED**${m.retraction_notice ? `: ${m.retraction_notice}` : ""}${m.retraction_doi ? ` (notice: https://doi.org/${m.retraction_doi})` : ""}`
      : null,
    m.expression_of_concern ? "**Expression of concern** on this article" : null,
  ].filter(Boolean);
  const passages = [m, ...g.rest].map((p, i) => {
    const facts = [
      p.section ? `section: ${p.section}` : null,
      side === "answer"
        ? `answers the question: ${conf(p, side).toFixed(2)}`
        : `confidence it ${side === "for" ? "supports" : "refutes"}: ${conf(p, side).toFixed(2)}`,
      p.population && p.population !== "unknown" ? `population: ${p.population}` : null,
      p.direct != null ? `directness: ${p.direct.toFixed(2)}` : null,
    ].filter(Boolean);
    return `Passage ${i + 1} (${facts.join(" · ")})\n\n${quotedPassage(p)}`;
  });
  return [
    `### ${n}. ${m.title ?? "Untitled"}`,
    meta.map((l) => `- ${l}`).join("\n"),
    ...passages,
  ].join("\n\n");
}

/** The question and every passage on each side, as markdown for an agent. */
function evidenceMarkdown(
  d: ClaimSupportView,
  question: string,
  mode: EvidenceMode,
  sort: SortKey,
  opts: { minConf: string; net: number; rules?: string },
): string {
  const sides = SUMMARY_SIDES[mode];
  const prefix: Record<Side, string> = { for: "S", against: "R", answer: "A" };
  const totals = sides
    .map((s) => {
      const items = sideItems(d, s);
      return `${EXPORT_SIDE_TITLES[s]}: ${plural(articleCount(items), "article")}, ${plural(items.length, "passage")}`;
    })
    .join(" · ");
  const head = [
    `# Evidence: ${question}`,
    [
      `- ${mode === "question" ? "Question" : "Claim"}: "${question}"`,
      `- Retrieved: ${new Date().toLocaleDateString("en-CA")} from GXL Paperclip Evidence API (${window.location.origin}/evidence-api)`,
      `- Search: ${opts.net} passages per source across PMC, arXiv, bioRxiv, medRxiv and abstracts; confidence ≥ ${opts.minConf}`,
      ...(opts.rules ? [`- Rules: ${opts.rules}`] : []),
      `- ${totals}`,
    ].join("\n"),
    "How to read this: each passage is quoted verbatim from the article; the **bold** text is the span GXL's model judged to " +
      (mode === "question" ? "answer the question" : "support or refute the claim") +
      ". Confidence is the model's probability (0–1). Evidence weight ranks articles by confidence × study design × citations. " +
      "Treat retracted articles and those with an expression of concern as unreliable. Cite articles by their number (e.g. S3, R1).",
  ];
  const body = sides.map((s) => {
    const groups = groupArticles(sideItems(d, s), s, sort);
    const articles = groups.map((g, i) => exportArticle(g, s, `${prefix[s]}${i + 1}`));
    return [
      `## ${EXPORT_SIDE_TITLES[s]} (${plural(groups.length, "article")})`,
      ...(articles.length ? articles : ["None found."]),
    ].join("\n\n");
  });
  return [...head, ...body].join("\n\n") + "\n";
}

export function CopyButton({ build, label, title }: { build: () => string; label: string; title: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const t = setTimeout(() => setState("idle"), 1800);
    return () => clearTimeout(t);
  }, [state]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(build());
      setState("copied");
    } catch {
      setState("failed");
    }
  };
  return (
    <button
      type="button"
      className="ch-view"
      onClick={() => void copy()}
      title={title}
    >
      {state === "copied" ? "Copied" : state === "failed" ? "Couldn't copy" : label}
    </button>
  );
}

const DEFAULT_SORT: SortKey = "weight";

const SUMMARY_SIDES: Record<EvidenceMode, Side[]> = {
  claim: ["for", "against"],
  question: ["answer"],
};

const SUMMARY_SIDE_NAMES: Record<Side, ClaimSummarySource["side"]> = {
  for: "supports",
  against: "refutes",
  answer: "answers",
};

function Faq() {
  return (
    <div className="ch-docs ch-faq" id="ch-faq">
      <h2>What is the confidence score?</h2>
      <p>
        Each retrieved passage is read by a GXL trained and calibrated model that judges whether
        it supports the claim, refutes it, or neither. The confidence score (0&ndash;1) is how
        sure the model is of that judgment. Only passages at or above the bar you set (default
        0.8) count as supporting or refuting; lower it to see more borderline passages, raise it
        to keep only clear-cut ones.
        {QUESTION_MODE_ENABLED
          ? " For a question, it is how sure the model is that the passage answers it."
          : null}
      </p>
      <p>
        It measures how directly a passage speaks to your claim, not how good the study is. The
        study-design badge, citation count and retraction flags come from the paper&rsquo;s
        metadata; they describe the paper, but they do not check how well the study was done.
      </p>
      <h2>What does &ldquo;Suggested order&rdquo; mean?</h2>
      <p>
        Suggested order is the default order of the results: a suggestion of where to start
        reading, not a rating of the evidence. Each passage gets a sort score from the
        model&rsquo;s confidence, adjusted for the study design and for how often the paper is
        cited (the API returns it as <code>weight</code>). Papers are listed by their
        highest-scoring passage.
      </p>
      <ul>
        <li>
          <strong>Design</strong> can lower the score by up to a third. Meta-analyses and
          systematic reviews come first, then randomized controlled trials, cohort,
          case-control and cross-sectional studies; narrative reviews and unclassified papers
          sit in the middle; ecological studies, opinion pieces, passive-surveillance reports,
          animal and in vitro studies, and case reports come last.
        </li>
        <li>
          <strong>Citations</strong> can lower it by up to a quarter, on a log scale that tops
          out around 10,000 citations (from GXL&rsquo;s citation graph).
        </li>
        <li>
          Papers with an expression of concern lose 40%. Retracted papers always go to the
          bottom, under their own divider.
        </li>
      </ul>
      <p>
        The order does not check how well a study was done: a small or badly run trial still
        counts as a trial. Read the passages, and the papers, before you rely on them. To sort by
        one thing only, choose Support, Citations or Date. Publication date is not part of the
        score; sort by Date for the newest first.
        {AUTHOR_H_INDEX_ENABLED ? (
          <>
            {" "}
            Sort by Author h-index to put papers with the most-cited authors first: each paper
            counts as the highest h-index among its authors, computed from GXL&rsquo;s citation
            graph with authors matched across name variants. It is not part of the score either.
          </>
        ) : null}
      </p>
      <h2>Where does the Evidence API get its information?</h2>
      <ul>
        <li>
          <strong>Literature:</strong> full-text passages from PubMed Central, bioRxiv and
          medRxiv, and arXiv, plus a collection of paper abstracts: 360M+ passages in all.
        </li>
        <li>
          <strong>Retrieval:</strong> hybrid search. A vector (semantic) search finds the passages
          closest in meaning to your input, and a keyword search adds strong matches it missed.
          The first search reads about 120 passages; &ldquo;Widen the net&rdquo; reads more.
        </li>
        <li>
          <strong>Judgment:</strong> every passage is read by GXL&rsquo;s model, which
          marks the exact sentences that support{QUESTION_MODE_ENABLED ? ", refute or answer" : " or refute"}.
        </li>
        <li>
          <strong>Citations:</strong> counts come from GXL&rsquo;s citation graph.
        </li>
        <li>
          <strong>Credibility:</strong> retractions and expressions of concern come from the
          Retraction Watch database; study design comes from the title, the article type, and a
          read of the title and abstract by the same model.
        </li>
      </ul>
      {QUESTION_MODE_ENABLED ? (
        <>
          <h2>Can I ask a question instead of a claim?</h2>
          <p>
            Yes. Questions that ask what, how, why, which or when (&ldquo;What is the mechanism of
            action of semaglutide?&rdquo;) get a list of answers, each with the answering text
            highlighted in its passage. A quick check by the model decides which kind of input you
            typed; yes/no questions are treated as claims. You can switch with &ldquo;Treat as a
            claim instead&rdquo; or &ldquo;Treat as a question instead&rdquo; above the results.
          </p>
          <h2>How are answers grouped?</h2>
          <p>
            Answers that say the same thing are grouped, so you see the distinct answers first. The
            model compares each answer with a handful of diverse ones and decides which, if any, it
            matches; numbers are compared after converting units. Each group&rsquo;s label is the
            exact wording of its most direct, best supported answer, followed by how many independent
            papers give it, their study designs and years, and who was studied (people, animals or
            lab work). Groups follow the suggested order, with more independent
            papers counting for more; answers that fit no group are under &ldquo;Other
            answers&rdquo;. Numeric questions (doses, half-lives, durations) also get a plot of
            every reported value. &ldquo;Show all answers (flat)&rdquo; lists every answer instead.
          </p>
        </>
      ) : null}
      <h2>Do you store my queries?</h2>
      <p>
        Queries you run on this page are logged, like other Paperclip queries. Each logged query
        also gets a permalink: the page address changes to it, and anyone with the link can see
        the results. If you are logged in, tick &ldquo;Don&rsquo;t store query&rdquo; next to
        the search button to keep them out of the log and get no link. Queries sent to the API with
        an API key are not stored, unless the request asks for a permalink: then the claim and
        the results are saved for the link, but the query is not logged.{" "}
        {SUMMARIES_IN_HOUSE
          ? "Summaries are written by an open model that we host ourselves; your query and the evidence are not sent to any third-party AI provider."
          : "Summaries are written by a third-party language model, which receives the query and the passages to summarize."}{" "}
        To
        limit use without an account, we also keep a one-way hash of your IP address for 24
        hours.
      </p>
    </div>
  );
}

export function ClaimHunterPage() {
  const { user, loading: authLoading } = useAuth();
  const loggedOut = !authLoading && !user;
  /** Logged out: the free query is used up, so searching (and widening) needs an account. */
  const [anonUsed, setAnonUsed] = useState(false);
  const queryClient = useQueryClient();
  /** Which logged-out action last asked for an account; shows the login prompt next to it. */
  const [needLogin, setNeedLogin] = useState<"widen" | "summary" | null>(null);
  useEffect(() => setAnonUsed(readAnonUsed()), []);
  const visitSent = useRef(false);
  useEffect(() => {
    if (authLoading || visitSent.current) return;
    visitSent.current = true;
    void recordVisit(visitReferrer(), !!user);
    if (!user) markEvidenceSignupSource();
  }, [authLoading, user]);
  const [aboutPanel, setAboutPanel] = useState<"faq" | "api" | null>(null);
  const locked = loggedOut && anonUsed;
  const [claim, setClaim] = useState("");
  const claimRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = claimRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [claim]);
  const [searched, setSearched] = useState("");
  const [rules, setRules] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [searchedRules, setSearchedRules] = useState("");
  const [net, setNet] = useState(NET_STEPS[0]);
  const [minConf, setMinConf] = useState("0.8");
  const [judge, setJudge] = useState<JudgeModel>("e2");
  /** Signed in only: keep this page's queries out of the query log (and give them no permalink). */
  const [noLog, setNoLog] = useState(false);
  const { permalinkId } = useParams();
  const navigate = useNavigate();
  const base = useEvidenceBase();
  /** The id of the shown result's permalink; null when it has none. */
  const [permalink, setPermalink] = useState<string | null>(null);
  /** Set to the link a run just made, so moving the URL to it doesn't reload it. */
  const ownLink = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ClaimSupportView | null>(null);
  const [tab, setTab] = useState<Side>("for");
  const [sort, setSort] = useState<SortKey>(DEFAULT_SORT);
  const [countRetracted, setCountRetracted] = useState(false);
  /** Question mode: every answer as one list instead of answer groups. */
  const [flat, setFlat] = useState(false);
  const [pop, setPop] = useState<PopKey | null>(null);

  const [summary, setSummary] = useState<SummaryState | null>(null);
  const summaryAbort = useRef<AbortController | null>(null);
  const [flash, setFlash] = useState<number | null>(null);

  const mode: EvidenceMode = QUESTION_MODE_ENABLED ? (data?.mode ?? "claim") : "claim";
  const side: Side = QUESTION_MODE_ENABLED && mode === "question" ? "answer" : tab;

  const groups = useMemo(() => {
    if (!data) return [];
    return groupArticles(sideItems(data, side), side, sort);
  }, [data, side, sort]);

  const answerGroups = useMemo(() => {
    if (!data?.groups || mode !== "question") return null;
    const answers = data.answers ?? [];
    const keep = (i: number) => i < answers.length && (!pop || popOf(answers[i]) === pop);
    const shown = data.groups
      .map((g: AnswerGroup) => ({ label: g.label, items: g.members.filter(keep).map((i) => answers[i]) }))
      .filter((g) => g.items.length);
    const other = (data.other ?? []).filter(keep).map((i) => answers[i]);
    return { groups: shown, other, pops: popCounts(answers) };
  }, [data, mode, pop]);
  const grouped = answerGroups != null && !flat;

  const refNumbers = useMemo(() => {
    const out = new Map<string, number>();
    summary?.refs.forEach((r, n) => out.set(`${r.side}|${r.key}`, n));
    return out;
  }, [summary?.refs]);

  useEffect(() => () => summaryAbort.current?.abort(), []);

  useEffect(() => {
    if (flash == null) return;
    const el = document.getElementById(`ch-ref-${flash}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("flash");
    void el.offsetWidth;
    el.classList.add("flash");
    setFlash(null);
  }, [flash, tab, groups]);

  const clearSummary = () => {
    summaryAbort.current?.abort();
    summaryAbort.current = null;
    setSummary(null);
  };

  const summarize = async () => {
    if (!data) return;
    if (loggedOut) {
      setNeedLogin("summary");
      return;
    }
    const refs = new Map<number, SummaryRef>();
    const sources: ClaimSummarySource[] = [];
    const perSide = QUESTION_MODE_ENABLED && mode === "question" ? 2 * SUMMARY_PER_SIDE : SUMMARY_PER_SIDE;
    for (const s of SUMMARY_SIDES[mode]) {
      const items = sideItems(data, s);
      const kept = groupArticles(items, s, sort).filter(
        (g) => countRetracted || !g.best.retracted,
      );
      for (const g of kept.slice(0, perSide)) {
        const n = sources.length + 1;
        refs.set(n, { side: s, key: articleKey(g.best) });
        sources.push({
          n,
          side: SUMMARY_SIDE_NAMES[s],
          title: g.best.title ?? null,
          authors: g.best.authors ?? null,
          journal: g.best.journal ?? null,
          year: g.best.pub_year ?? null,
          design: designLabel(g.best),
          retracted: !!g.best.retracted,
          text: sourceText(g.best),
        });
      }
    }
    if (!sources.length) return;
    summaryAbort.current?.abort();
    const ctrl = new AbortController();
    summaryAbort.current = ctrl;
    setSummary({ text: "", streaming: true, error: null, refs });
    try {
      let text = "";
      await summarizeEvidence(
        { claim: searched, sources },
        (piece) => {
          text += piece;
        },
        ctrl.signal,
      );
      setSummary((s) => (s ? { ...s, text, streaming: false } : s));
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setSummary((s) =>
        s
          ? {
              ...s,
              streaming: false,
              error:
                err instanceof ApiError ? err.message : "Couldn't reach the summary service.",
            }
          : s,
      );
    }
  };

  const goToRef = (n: number) => {
    const ref = summary?.refs.get(n);
    if (!ref) return;
    if (ref.side !== "answer") setTab(ref.side);
    else setFlat(true);
    setFlash(n);
  };

  const showResult = (
    d: ClaimSupportView,
    text: string,
    size: number,
    fresh: boolean,
    appliedRules = "",
  ) => {
    setData(d);
    setSearched(text);
    setSearchedRules(appliedRules);
    setNet(size);
    if (!fresh) return;
    setTab(
      articleCount(d.refutes, countRetracted) > articleCount(d.supports, countRetracted)
        ? "against"
        : "for",
    );
    setSort(DEFAULT_SORT);
    setFlat(false);
    setPop(null);
  };

  /** Each logged run gets its own link; an opted-out run leaves the plain page URL. */
  const showPermalink = (link: string | null) => {
    setPermalink(link);
    ownLink.current = link;
    if (link) navigate(`${base}/r/${link}`, { replace: true });
    else if (permalinkId) navigate(base, { replace: true });
  };

  useEffect(() => {
    if (!permalinkId) {
      // The URL lost a link this page did not remove: a link out to the plain page.
      if (ownLink.current) {
        clearSummary();
        setData(null);
        setError(null);
        setPermalink(null);
        ownLink.current = null;
        setClaim("");
      }
      return;
    }
    if (permalinkId === ownLink.current) return;
    let cancelled = false;
    let loading = true;
    clearSummary();
    setData(null);
    setError(null);
    setBusy(true);
    fetchPermalink(permalinkId)
      .then((saved) => {
        if (cancelled) return;
        const size = saved.params.top_n ?? NET_STEPS[0];
        setClaim(saved.claim);
        setMinConf(String(saved.params.min_conf ?? 0.8));
        const savedRules = RULES_ENABLED ? (saved.params.rules ?? "") : "";
        setRules(savedRules);
        setRulesOpen(!!savedRules);
        if (JUDGE_TOGGLE_ENABLED) setJudge(saved.view.judge ?? saved.params.judge ?? "e2");
        showResult(saved.view, saved.claim, size, true, savedRules);
        setPermalink(permalinkId);
        ownLink.current = permalinkId;
      })
      .catch((err) => {
        if (cancelled) return;
        setPermalink(null);
        setError(err instanceof ApiError ? err.message : "Couldn't load this link.");
      })
      .finally(() => {
        loading = false;
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
      if (loading) setBusy(false);
    };
    // Load once per link; showResult reads the latest retracted toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permalinkId]);

  const run = async (
    text: string,
    size: number,
    widening: boolean,
    runMode: "auto" | EvidenceMode = QUESTION_MODE_ENABLED ? "auto" : "claim",
  ) => {
    const anonymous = loggedOut;
    if (anonymous && (widening || anonUsed)) {
      if (widening) setNeedLogin("widen");
      return;
    }
    setBusy(true);
    setError(null);
    setNeedLogin(null);
    clearSummary();
    if (!widening) {
      setData(null);
      showPermalink(null);
    }
    const runRules = RULES_ENABLED ? rules.trim() : "";
    try {
      const { view, permalink: link } = await huntClaim(
        {
          claim: text,
          top_n: size,
          min_conf: Math.min(Math.max(Number(minConf) || 0.8, 0.5), 0.99),
          limit: RESULT_LIMIT,
          include_context: true,
          mode: QUESTION_MODE_ENABLED ? runMode : "claim",
          ...(runRules ? { rules: runRules } : {}),
          ...(JUDGE_TOGGLE_ENABLED ? { judge } : {}),
        },
        { anonymous, noLog: noLog && !anonymous },
      );
      if (anonymous) {
        markAnonUsed();
        setAnonUsed(true);
      } else {
        void queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
      }
      showResult(view, text, size, !widening, runRules);
      showPermalink(link);
    } catch (err) {
      if (anonymous && err instanceof ApiError && err.status === 429) {
        markAnonUsed();
        setAnonUsed(true);
        return;
      }
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong reaching the evidence service.",
      );
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = claim.trim();
    if (!trimmed || busy || locked) return;
    void run(trimmed, NET_STEPS[0], false);
  };

  const nextNet = NET_STEPS.find((n) => n > net);
  const otherMode: EvidenceMode = mode === "question" ? "claim" : "question";

  const tabLabel = (items: EvidenceItem[]): string => {
    const shown = articleCount(items, countRetracted);
    const retracted = articleCount(items) - articleCount(items, false);
    return retracted && !countRetracted ? `${shown} (+${retracted} retracted)` : `${shown}`;
  };
  const shownItems = data
    ? QUESTION_MODE_ENABLED && mode === "question"
      ? (data.answers ?? [])
      : [...data.supports, ...data.refutes]
    : [];
  const anyRetracted = shownItems.some((m) => m.retracted);
  const firstRetracted = groups.findIndex((g) => g.best.retracted);

  const about = (
    <div className="ch-about">
      <div className="ch-about-links">
        <button
          type="button"
          aria-expanded={aboutPanel === "faq"}
          aria-controls="ch-faq"
          onClick={() => setAboutPanel((p) => (p === "faq" ? null : "faq"))}
        >
          FAQ
        </button>
        <span className="ch-dot" aria-hidden="true">
          ·
        </span>
        <button
          type="button"
          aria-expanded={aboutPanel === "api"}
          aria-controls="ch-api-docs"
          onClick={() => setAboutPanel((p) => (p === "api" ? null : "api"))}
        >
          How to use the API
        </button>
      </div>
      {aboutPanel === "faq" ? <Faq /> : null}
      {aboutPanel === "api" ? (
        <>
          <ApiDocs />
          <CopyForAgent />
        </>
      ) : null}
    </div>
  );

  return (
    <div className="ch-root">
      <style>{CSS}</style>
      <div className="ch-wrap">
        <header>
          <h1 className="ch-kicker">GXL Paperclip · Evidence API</h1>
          <p className="ch-lede">
            {QUESTION_MODE_ENABLED
              ? "Find evidence that supports (or refutes) a given claim, or answers to an open question."
              : "Find evidence that supports (or refutes) a given claim."}
          </p>
          <p className="ch-source">
            Each claim is run against 360M+ passages across PMC, arXiv, bioRxiv &amp; medRxiv, and
            abstracts.
          </p>
        </header>
        <form className="ch-form" onSubmit={(e) => void submit(e)}>
          <textarea
            ref={claimRef}
            className="ch-claim"
            rows={1}
            value={claim}
            maxLength={590}
            onChange={(e) => setClaim(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }}
            placeholder={
              QUESTION_MODE_ENABLED
                ? "Enter a claim or a question — e.g. Metformin improves survival in pancreatic cancer patients, or What is the mechanism of action of semaglutide?"
                : "Enter a claim — e.g. Metformin improves survival in pancreatic cancer patients"
            }
          />
          <div className="ch-bar">
            <div className="ch-bar-left">
              <span className="ch-opts">
                <label>
                  confidence ≥{" "}
                  <select value={minConf} onChange={(e) => setMinConf(e.target.value)}>
                    {confOptions(minConf).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
              </span>
              {JUDGE_TOGGLE_ENABLED ? (
                <span className="ch-opts">
                  <label>
                    model{" "}
                    <select value={judge} onChange={(e) => setJudge(e.target.value as JudgeModel)}>
                      {(Object.keys(JUDGE_LABELS) as JudgeModel[]).map((j) => (
                        <option key={j} value={j}>
                          {JUDGE_LABELS[j]}
                        </option>
                      ))}
                    </select>
                  </label>
                </span>
              ) : null}
              {/* Disabled inputs can't take focus, so the wrapper does, to reach the tip's link. */}
              <span
                className="ch-nolog"
                tabIndex={loggedOut ? 0 : undefined}
                aria-describedby={loggedOut ? "ch-nolog-tip" : undefined}
              >
                <label className="ch-toggle">
                  <input
                    type="checkbox"
                    checked={noLog && !!user}
                    disabled={!user}
                    onChange={(e) => setNoLog(e.target.checked)}
                  />
                  Don&rsquo;t store query
                </label>
                {loggedOut ? (
                  <span className="ch-nolog-tip" id="ch-nolog-tip">
                    <span>
                      To enable, <Link to={loginUrl(base)}>log in</Link>.
                    </span>
                  </span>
                ) : null}
              </span>
            </div>
            <button
              className={`ch-go${locked ? " locked" : ""}`}
              type="submit"
              disabled={busy || locked}
            >
              Find evidence
            </button>
          </div>
          {RULES_ENABLED ? (
            rulesOpen ? (
              <textarea
                className="ch-rules"
                rows={3}
                value={rules}
                maxLength={1000}
                aria-label="Search rules"
                placeholder={RULES_PLACEHOLDER}
                onChange={(e) => setRules(e.target.value)}
              />
            ) : (
              <button type="button" className="ch-rules-toggle" onClick={() => setRulesOpen(true)}>
                + Add rules
              </button>
            )
          ) : null}
          {locked ? (
            <div className="ch-locked">
              <LoginPrompt />
            </div>
          ) : null}
        </form>
        {busy && !data ? <div className="ch-status">Reading the literature…</div> : null}
        {error ? <div className="ch-status">{error}</div> : null}
        {data ? null : about}
        {data ? (
          <div className="ch-results">
            {QUESTION_MODE_ENABLED && data.mode ? (
              <div className="ch-mode">
                {mode === "question" ? "Answering as an open question." : "Checking as a claim."}{" "}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void run(searched, NET_STEPS[0], false, otherMode)}
                >
                  {mode === "question" ? "Treat as a claim instead" : "Treat as a question instead"}
                </button>
              </div>
            ) : null}
            {busy ? (
              <div className="ch-widen">
                Widening the net to {nextNet} passages per source…
              </div>
            ) : nextNet ? (
              <div className="ch-widen">
                Didn&rsquo;t find what you were looking for?{" "}
                <button type="button" onClick={() => void run(searched, nextNet, true, mode)}>
                  Widen the net
                </button>{" "}
                ({nextNet} passages per source instead of {net})
                {needLogin === "widen" ? (
                  <div>
                    <LoginPrompt />
                  </div>
                ) : null}
              </div>
            ) : null}
            {summary ? (
              <div className="ch-summary-wrap">
                <SummaryText summary={summary} onCite={goToRef} />
              </div>
            ) : !busy && shownItems.length ? (
              <div className="ch-summarize-row">
                <button type="button" className="ch-copy-agent" onClick={() => void summarize()}>
                  {QUESTION_MODE_ENABLED && mode === "question" ? "Summarize answers" : "Summarize evidence"}
                </button>
                {needLogin === "summary" ? (
                  <div>
                    <LoginPrompt />
                  </div>
                ) : null}
              </div>
            ) : null}
            {searchedRules ? (
              <p className="ch-rules-used">
                <b>Rules applied:</b> {searchedRules}
              </p>
            ) : null}
            {JUDGE_TOGGLE_ENABLED && data.judge ? (
              <p className="ch-judge-used">
                <b>Judged by:</b> {JUDGE_LABELS[data.judge]}
              </p>
            ) : null}
            <div className="ch-controls">
              <div className="ch-tabs">
                {QUESTION_MODE_ENABLED && mode === "question" ? (
                  <span className="ch-tab answer on">Answers · {tabLabel(shownItems)}</span>
                ) : (
                  <>
                    <button
                      className={`ch-tab for ${tab === "for" ? "on" : ""}`}
                      onClick={() => setTab("for")}
                      type="button"
                    >
                      Supporting · {tabLabel(data.supports)}
                    </button>
                    <button
                      className={`ch-tab against ${tab === "against" ? "on" : ""}`}
                      onClick={() => setTab("against")}
                      type="button"
                    >
                      Refuting · {tabLabel(data.refutes)}
                    </button>
                  </>
                )}
              </div>
              <div className="ch-right">
                {anyRetracted ? (
                  <label className="ch-toggle">
                    <input
                      type="checkbox"
                      checked={countRetracted}
                      onChange={(e) => setCountRetracted(e.target.checked)}
                    />
                    count retracted
                  </label>
                ) : null}
                {QUESTION_MODE_ENABLED && answerGroups ? (
                  <button type="button" className="ch-view" onClick={() => setFlat(!flat)}>
                    {flat ? "Group the answers" : "Show all answers (flat)"}
                  </button>
                ) : null}
                {permalink ? (
                  <CopyButton
                    label="Copy link"
                    title="Copy a permanent link to these results"
                    build={() => permalinkUrl(base, permalink)}
                  />
                ) : null}
                {shownItems.length ? (
                  <CopyButton
                    label="Copy all evidence"
                    title="Copy the question and every passage, with its paper's metadata, as markdown to paste into any agent"
                    build={() =>
                      evidenceMarkdown(data, searched, mode, sort, {
                        minConf: Math.min(Math.max(Number(minConf) || 0.8, 0.5), 0.99).toString(),
                        net,
                        rules: searchedRules,
                      })
                    }
                  />
                ) : null}
                <span className="ch-time">
                  {(data.timing_ms.total / 1000).toFixed(1)}s
                </span>
                <label className="ch-sorter">
                  sort by{" "}
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortKey)}
                  >
                    {SORT_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {sortLabel(k, side)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            {QUESTION_MODE_ENABLED && grouped && answerGroups ? (
              <>
                {answerGroups.pops.length ? (
                  <div className="ch-chips">
                    <button
                      type="button"
                      className={`ch-chip${pop ? "" : " on"}`}
                      onClick={() => setPop(null)}
                    >
                      All
                    </button>
                    {answerGroups.pops.map(([p, n]) => (
                      <button
                        key={p}
                        type="button"
                        className={`ch-chip${pop === p ? " on" : ""}`}
                        onClick={() => setPop(pop === p ? null : p)}
                      >
                        {POP_LABELS[p]} · {n}
                      </button>
                    ))}
                  </div>
                ) : null}
                {data.distribution ? (
                  <DotPlot dist={data.distribution} answers={data.answers ?? []} pop={pop} />
                ) : null}
                {answerGroups.groups.map((g, i) => (
                  <AnswerGroupView
                    key={`${pop ?? "all"}-${i}`}
                    label={g.label}
                    items={g.items}
                    sort={sort}
                    refNumbers={refNumbers}
                  />
                ))}
                {answerGroups.other.length ? (
                  <details className="ch-details ch-other">
                    <summary>Other answers ({articleCount(answerGroups.other)})</summary>
                    {groupArticles(answerGroups.other, "answer", sort).map((a, i) => (
                      <Card key={i} m={a.best} rest={a.rest} side="answer" />
                    ))}
                  </details>
                ) : null}
                {!answerGroups.groups.length && !answerGroups.other.length ? (
                  <div className="ch-none">No answers from this population.</div>
                ) : null}
              </>
            ) : groups.length ? (
              groups.map((g, i) => (
                <div key={i}>
                  {i === firstRetracted ? (
                    <div className="ch-retracted-rule">
                      Retracted · shown for reference
                      {countRetracted ? "" : ", not counted"}
                    </div>
                  ) : null}
                  <Card
                    m={g.best}
                    rest={g.rest}
                    side={side}
                    refNo={refNumbers.get(`${side}|${articleKey(g.best)}`)}
                    alsoQuotedIn={g.alsoQuotedIn}
                  />
                </div>
              ))
            ) : (
              <div className="ch-none">
                {QUESTION_MODE_ENABLED && mode === "question"
                  ? "No passage answered the question above the confidence bar."
                  : "Nothing above the confidence bar."}
              </div>
            )}
            {about}
            <QueryFeedback
              key={`${mode}|${searched}`}
              query={searched}
              loggedOut={loggedOut}
              mode={QUESTION_MODE_ENABLED ? (data.mode ?? null) : "claim"}
              counts={
                QUESTION_MODE_ENABLED && mode === "question"
                  ? { answers: articleCount(shownItems) }
                  : { supporting: articleCount(data.supports), refuting: articleCount(data.refutes) }
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
